import React from 'react';
import styled from 'styled-components';
import { Box } from '@mui/material';
import { NOMINATION_TEXT } from '../../lang';
import { type SupervisorFormData } from '../../../graphql';
import { Checkbox, TextField } from '../../../../../components';
import { DeclarationLabels } from '../../../../Nomination/components/NominationForm/Declaration/DeclarationBox';
import {
  NominationDeclaration,
  type DeclarationValues,
} from '../../../../Nomination/graphql/NominationForm';
import { DECLARATION_TEXT } from './lang';
import type { SupervisorRoleTypes } from '../../../../Nomination';
import { PAGE_TEXT } from '../../../../Nomination/components/lang';

type DeclarationFormProps = {
  data?: DeclarationValues[];
  supervisorsData: SupervisorFormData[];
  grayCheckbox?: boolean;
};

const DECLARATION = NOMINATION_TEXT.DECLARATION;
const coDeclarationLabels = [
  PAGE_TEXT.DECLARATION.FORALL.DECLARATION_1,
  PAGE_TEXT.DECLARATION.FORALL.DECLARATION_2,
];
export const DeclarationForm: React.FC<DeclarationFormProps> = (
  props: DeclarationFormProps,
) => {
  const {
    data: declarationData = [],
    supervisorsData,
    grayCheckbox = false,
  } = props;
  const displaySmall = grayCheckbox;

  const coSupervisorsData = supervisorsData?.slice(1, supervisorsData.length);

  const handleDisplayCoSupDeclaration = (
    index: number,
    type: SupervisorRoleTypes,
    fullName: string,
    confirmedAsCoSupervisor: boolean,
    coSupervisorDeclarationRemark: string,
  ): React.ReactNode => {
    const preIdx = index;
    const coSupDeclarationData = [
      {
        confirmed: confirmedAsCoSupervisor
          ? coSupervisorDeclarationRemark === ''
          : false,
        justification: coSupervisorDeclarationRemark,
      },
      {
        confirmed: confirmedAsCoSupervisor,
        justification: '',
      },
    ];

    return (
      <div key={preIdx} className={`w-full ${displaySmall ? 'text-sm' : ''}`}>
        <hr className="my-8" />
        <h2 className={`text-[#224295] mb-2 ${displaySmall ? 'italic' : ''}`}>
          {DECLARATION_TEXT[type]} - {fullName}
        </h2>
        {coSupDeclarationData.map((dec, index) => {
          const contentLabel = coDeclarationLabels[index];
          const { confirmed, justification } = dec;
          return (
            <div key={`${preIdx}-${index}`} className="px-4">
              <Checkbox
                value={confirmed}
                size="small"
                disabled
                disabledGray={grayCheckbox}
                label={
                  <div
                    className={`mt-2 ${displaySmall ? 'text-sm' : ''}`}
                    dangerouslySetInnerHTML={{
                      __html: contentLabel,
                    }}
                  />
                }
                sx={{
                  alignItems: 'flex-start',
                }}
              />
              {!confirmed && index === 0 && (
                <div className="w-full px-6 mt-6 mb-2">
                  <TextField
                    label={PAGE_TEXT.DECLARATION.FORALL.DECLARATION_1_SUB}
                    fullWidth
                    disabled
                    size={displaySmall ? 'small' : 'medium'}
                    value={justification}
                    minRows={3}
                    InputLabelProps={{ shrink: true }}
                    multiline
                    required
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Container>
      <h2 className={`text-[#224295] mb-2 ${displaySmall ? 'italic' : ''}`}>
        {DECLARATION_TEXT.CHIEF_SUPERVISOR}
      </h2>
      <div className="px-4">
        {declarationData?.map((declaration, index) => {
          const contentLabel = DeclarationLabels[declaration.content];
          return (
            <div key={index}>
              <Checkbox
                size="small"
                label={
                  <div
                    className={`mt-2 ${displaySmall ? 'text-sm' : ''}`}
                    dangerouslySetInnerHTML={{
                      __html: contentLabel,
                    }}
                  />
                }
                value={declaration.confirmed}
                disabled
                disabledGray={grayCheckbox}
                sx={{ alignItems: 'flex-start' }}
              />
              {!declaration.confirmed &&
                declaration.content ===
                  NominationDeclaration.NO_INTEREST_CONFLICT && (
                  <Box className="text-Box">
                    <TextField
                      label={DECLARATION.FORALL.DECLARATION_1_SUB}
                      className="w-full"
                      InputLabelProps={{ shrink: true }}
                      value={declarationData[index]?.justification}
                      minRows={3}
                      multiline
                      disabled
                    />
                  </Box>
                )}
              {!declaration.confirmed &&
                declaration.content ===
                  NominationDeclaration.NO_RESEARCH_COLLABORATION_WITH_PARTNER_CHIEF_SUPERVISOR && (
                  <Box className="text-Box">
                    <TextField
                      label={DECLARATION.FORALL.DECLARATION_1_SUB}
                      className="w-full"
                      InputLabelProps={{ shrink: true }}
                      value={declarationData[index]?.justification}
                      minRows={3}
                      multiline
                      disabled
                    />
                  </Box>
                )}
            </div>
          );
        })}
      </div>
      {coSupervisorsData?.map((data, index) => {
        const {
          type,
          givenName,
          surname,
          coSupervisorDeclarationRemark,
          confirmedAsCoSupervisor,
        } = data;
        return handleDisplayCoSupDeclaration(
          index,
          type,
          surname + ' ' + givenName,
          confirmedAsCoSupervisor ?? false,
          coSupervisorDeclarationRemark ?? '',
        );
      })}
    </Container>
  );
};

const Container = styled.div`
  padding: 0.5rem 1.5rem 1.5rem 1.5rem;

  .text-Box {
    margin: 0.75rem;
    margin-left: 2.25rem;
    margin-right: 2.25rem;
    width: 100%;
  }
`;
